import { assign, fromPromise, setup } from "xstate";

export type SearchEvent =
  | { type: "QUERY_CHANGED"; query: string }
  | { type: "CLEAR" }
  | { type: "RETRY" };

interface SearchContext {
  query: string;
  results: unknown[];
  error: string | null;
}

const DEBOUNCE_DELAY_MS = 300;

export const searchMachine = setup({
  // Schema Definition Pattern for XState v5 type inference
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion
  types: {} as {
    context: SearchContext;
    events: SearchEvent;
  },
  actors: {
    fetchResultsActor: fromPromise(async ({ input }: { input: { query: string } }) => {
      // Overridden in component
      console.log("Searching for", input.query);
      await Promise.resolve();
      return [];
    }),
  },
}).createMachine({
  id: "search",
  initial: "idle",
  context: {
    query: "",
    results: [],
    error: null,
  },
  states: {
    idle: {
      on: {
        QUERY_CHANGED: {
          target: "debouncing",
          actions: assign({
            query: ({ event }) => event.query,
          }),
        },
        CLEAR: {
          actions: assign({
            query: "",
            results: [],
          }),
        },
      },
    },
    debouncing: {
      after: {
        [DEBOUNCE_DELAY_MS]: [
          {
            target: "searching",
            guard: ({ context }) => context.query.length > 0,
          },
          { target: "idle" },
        ],
      },
      on: {
        QUERY_CHANGED: {
          target: "debouncing",
          actions: assign({
            query: ({ event }) => event.query,
          }),
          reenter: true,
        },
      },
    },
    searching: {
      invoke: {
        src: "fetchResultsActor",
        input: ({ context }) => ({ query: context.query }),
        onDone: {
          target: "idle",
          actions: assign({
            results: ({ event }) => event.output,
          }),
        },
        onError: {
          target: "error",
          actions: assign({
            error: ({ event }) => {
              const err = event.error;
              return err instanceof Error ? err.message : String(err);
            },
          }),
        },
      },
    },
    error: {
      on: {
        RETRY: "searching",
        QUERY_CHANGED: {
          target: "debouncing",
          actions: assign({
            query: ({ event }) => event.query,
          }),
        },
      },
    },
  },
});
